import React, {FC, useState} from "react"

import {IconTypes} from "@irati/ui/src/Icon"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Flex from "@irati/ui/src/Flex"
import FormattedMessage, {InjectIntl, injectIntl, useIntl} from "@irati/utils/lang"
import {Body, H6, Label} from "@irati/ui/src/Typography"
import {ReportingValuesAndCelebrations} from "@irati/types"
import {adminDashboardTracking} from "@irati-tracking/admin"
import Widget from "../Widget"
import BoldNumberSmallText from "../BoldNumberSmallText"
import CompanyValue from "./CompanyValue"
import ValuesAndCelebrationsModal from "./ValuesAndCelebrationsModal"

interface Props extends InjectIntl {
  valuesAndCelebrations: ReportingValuesAndCelebrations
}

const ValuesCelebrationsWidget: FC<Props> = ({valuesAndCelebrations, formatMessage}) => {
  const [showValuesAndCelebrationsModal, setShowValuesAndCelebrationsModal] = useState(false)
  const theme = useTheme()
  const topCelebration = valuesAndCelebrations?.topCelebration
  const celebrationName = topCelebration?.name
  const {formatNumber} = useIntl()

  const handleValuesAndCelebrationsModalOpen = () => {
    adminDashboardTracking.valuesCelebrationsInsights()
    setShowValuesAndCelebrationsModal(true)
  }

  return (
    <Widget
      title="admin.components.dashboard.reporting.valuesCelebrationsWidget.title"
      icon={IconTypes.companyBenefit}
      iconColor={theme.colors.primary1}
      onClick={handleValuesAndCelebrationsModalOpen}
      buttonText="admin.components.dashboard.reporting.valuesCelebrationsWidget.button">
      {valuesAndCelebrations?.totalValues > 0 || valuesAndCelebrations?.totalCelebrations > 0 ? (
        <div>
          {valuesAndCelebrations?.totalValues > 0 && (
            <Flex marginBottom={1}>
              <BoldNumberSmallText
                boldValue={formatNumber(valuesAndCelebrations.totalValues)}
                text="admin.components.dashboard.reporting.valuesCelebrationsWidget.topCelebration.values"
              />
            </Flex>
          )}
          {valuesAndCelebrations?.topValues?.length > 0 && (
            <Flex justifyContent="space-around" marginBottom={2}>
              {valuesAndCelebrations.topValues.map((v, i) => {
                return <CompanyValue key={v.uuid} value={v} ranking={i + 1} />
              })}
            </Flex>
          )}
          {valuesAndCelebrations?.totalCelebrations > 0 && (
            <Flex marginBottom={1}>
              <BoldNumberSmallText
                boldValue={formatNumber(valuesAndCelebrations.totalCelebrations!)}
                text="admin.components.dashboard.reporting.valuesCelebrationsWidget.topCelebration.celebrations"
              />
            </Flex>
          )}
          {topCelebration?.icon?.href && (
            <Flex gap={1}>
              <Flex alignItems="center">
                <img width="25px" height="25px" src={formatMessage({id: topCelebration.icon.href})} />
              </Flex>
              <div>
                <H6 display="inline">
                  <FormattedMessage id="admin.components.dashboard.reporting.valuesCelebrationsWidget.topCelebration.award" />
                </H6>
                <Label marginLeft={0.5}>
                  <FormattedMessage id={celebrationName} />
                </Label>
                <Body small wordBreak="break-word">
                  <FormattedMessage
                    id="admin.components.dashboard.reporting.valuesCelebrationsWidget.topCelebration.count"
                    values={{count: formatNumber(topCelebration.usageCount!)}}
                  />
                </Body>
              </div>
            </Flex>
          )}
        </div>
      ) : (
        <>
          <Flex justifyContent="center" marginBottom={1}>
            <img width="100px" src={formatMessage({id: "admin.components.dashboard.reporting.valuesCelebrationsWidget.img"})} />
          </Flex>
          <Body>
            <FormattedMessage id="admin.components.dashboard.reporting.valuesCelebrationsWidget.comingSoon.text" />
          </Body>
        </>
      )}
      <ValuesAndCelebrationsModal show={showValuesAndCelebrationsModal} onHide={() => setShowValuesAndCelebrationsModal(false)} />
    </Widget>
  )
}

export default injectIntl(ValuesCelebrationsWidget)
